import React from "react";
import {
    Box,
    Typography,
    MenuItem,
    Select,
    FormControl,
    RadioGroup,
    FormControlLabel,
    Radio,
    Grid,
    Button,
    IconButton,
    Divider,
    TextField,
    CircularProgress,
    FormHelperText,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import dayjs, { Dayjs } from "dayjs";
import { useMutation } from "@tanstack/react-query";
import { HighticketCloserForm } from "../../../../api/services";
import BoxInput from "../../../../Components/BoxInput";
import { toast } from "react-toastify";

const primary = "#0f63a5";

interface Props {
    onClose: () => void;
}

interface FormValues {
    name: string;
    email: string;
    phone: string;
    city: string;
    industry: string;
    dealSize: string;
    challenge: string;
    goal: string;
    commitment: string;
    startMonth: Dayjs | null;
}

const schema: yup.ObjectSchema<FormValues> = yup.object({
    name: yup.string().required("Full Name is required"),
    email: yup.string().email("Invalid email").required("Email is required"),
    phone: yup.string().required("Phone is required"),
    city: yup.string().required("City is required"),
    industry: yup.string().required("Industry is required"),
    dealSize: yup.string().required("Deal size is required"),
    challenge: yup.string().required("Challenge is required"),
    goal: yup.string().required("Goal is required"),
    commitment: yup.string().required("Commitment is required"),
    startMonth: yup
        .mixed<Dayjs>()
        .required("Start date is required")
        .nullable(),
});

const HighTicketForm: React.FC<Props> = ({ onClose }) => {
    const {
        control,
        handleSubmit,
        reset,
        formState: { errors },
    } = useForm<FormValues>({
        resolver: yupResolver(schema),
        defaultValues: {
            name: "",
            email: "",
            phone: "",
            city: "",
            industry: "",
            dealSize: "",
            challenge: "",
            goal: "",
            commitment: "",
            startMonth: null,
        },
    });

    const mutation = useMutation({
        mutationFn: HighticketCloserForm,
        onSuccess: () => {
            toast.success("Application Submitted Successfully");
            reset();
            onClose();
        },
        onError: (error: any) => {
            toast.error(error?.response?.data?.message?.[0] || "Submission failed");
        },
    });

    const onSubmit = (data: FormValues) => {
        const payload = {
            fullName: data.name,
            email: data.email,
            phone: data.phone,
            cityMarket: data.city,
            industry: data.industry,
            averageDealSize: data.dealSize,
            biggestClosingChallenge: data.challenge,
            improvementNextSixMonths: data.goal,
            readyToCommit: data.commitment,
            availableDate: dayjs(data.startMonth).toISOString(),
        };

        mutation.mutate(payload);
    };

 const luxuryField = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "16px",
    backgroundColor: "#ffffff",

    // ✅ USER TYPED TEXT COLOR
    "& input": {
      color: "#000",
    },

    "& textarea": {
      color: "#000",
    },

    // ✅ PLACEHOLDER COLOR
    "& input::placeholder": {
      color: "#9e9e9e",
      opacity: 1,
    },

    "& textarea::placeholder": {
      color: "#9e9e9e",
      opacity: 1,
    },

    "& fieldset": {
      borderColor: "rgba(15,99,165,0.15)",
    },
    "&:hover fieldset": {
      borderColor: primary,
    },
    "&.Mui-focused fieldset": {
      borderColor: primary,
      boxShadow: "0 0 0 4px rgba(15,99,165,0.08)",
    },
  },
};
    return (
        <Box
            onSubmit={handleSubmit(onSubmit)}
            sx={{
                p: { xs: 2, md: 6 },
                borderRadius: "32px",
                background: "linear-gradient(180deg, #ffffff 0%, #f7fbff 100%)",
                boxShadow: `0 40px 120px rgba(15,99,165,0.15)`,
                border: "1px solid rgba(15,99,165,0.12)",
            }}
        >
            <IconButton
                onClick={onClose}
                sx={{
                    position: "absolute",
                    top: 24,
                    right: 30,
                    background: "white"
                }}
            >
                <CloseIcon />
            </IconButton>

            <Typography variant="h4" sx={{ fontWeight: 700, mb: 5, mt: { xs: 2, md: "auto" } }}>
                High-Ticket Closer Application
            </Typography>

            <Grid container spacing={4}>
                <Grid item xs={12} md={6}>
                    <Controller
                        name="name"
                        control={control}
                        render={({ field }) => (
                            <BoxInput
                                sx={{ mt: 2, ...luxuryField }}
                                placeholder="Full Name"
                                fullWidth
                                {...field}
                                error={!!errors.name}
                                helperText={errors.name?.message}
                            />
                        )}
                    />

                    <Controller
                        name="email"
                        control={control}
                        render={({ field }) => (
                            <BoxInput
                                sx={{ mt: 2, ...luxuryField }}
                                placeholder="Email"
                                fullWidth
                                {...field}
                                error={!!errors.email}
                                helperText={errors.email?.message}
                            />
                        )}
                    />

                    <Controller
                        name="phone"
                        control={control}
                        render={({ field }) => (
                            <BoxInput
                                sx={{ mt: 2, ...luxuryField }}
                                placeholder="Phone"
                                fullWidth
                                {...field}
                                error={!!errors.phone}
                                helperText={errors.phone?.message}
                            />
                        )}
                    />


                    <Controller
                        name="city"
                        control={control}
                        render={({ field }) => (
                            <BoxInput
                            sx={{ mt: 2, ...luxuryField }}
                                placeholder="City / Market"
                                fullWidth
                                {...field}
                                error={!!errors.city}
                                helperText={errors.city?.message}
                            />
                        )}
                    />

                    {/* Industry Styled Like BoxInput */}
                    {/* <FormControl fullWidth sx={{ mt: 2, ...luxuryField }}>
                        <Select
                            name="industry"
                            value={formData.industry}
                            onChange={handleSelect}
                            displayEmpty
                            sx={{
                                color: formData.industry ? "#111" : "#9e9e9e",
                                "& .MuiSelect-select": {
                                    padding: "16.5px 14px",
                                },
                                fontSize: "14px",
                            }}
                            renderValue={(selected) => {
                                if (!selected) {
                                    return <span style={{ color: "#9e9e9e", fontSize: "14px", }}>Industry</span>;
                                }
                                return selected;
                            }}
                        >
                            <MenuItem disabled value="">
                                Industry
                            </MenuItem>
                            <MenuItem value="Real Estate">Real Estate</MenuItem>
                            <MenuItem value="Financial Services">Financial Services</MenuItem>
                            <MenuItem value="Coaching">Coaching</MenuItem>
                            <MenuItem value="Luxury Retail">Luxury Retail</MenuItem>
                            <MenuItem value="B2B Services">B2B Services</MenuItem>
                            <MenuItem value="Other">Other</MenuItem>
                        </Select>
                    </FormControl> */}
                    <Controller
                        name="industry"
                        control={control}
                        render={({ field }) => (
                            <FormControl
                                fullWidth
                                sx={{ mt: 2, ...luxuryField }}
                                error={!!errors.industry}
                            >
                                <Select
                                    {...field}
                                    displayEmpty
                                    sx={{
                                        color: field.value ? "#111" : "#9e9e9e",
                                        "& .MuiSelect-select": {
                                            padding: "16.5px 14px",
                                        },
                                        fontSize: "14px",
                                    }}
                                    renderValue={(selected) => {
                                        if (!selected) {
                                            return (
                                                <span style={{ color: "#9e9e9e", fontSize: "14px" }}>
                                                    Industry
                                                </span>
                                            );
                                        }
                                        return selected;
                                    }}
                                >
                                    <MenuItem disabled value="">
                                        Industry
                                    </MenuItem>
                                    <MenuItem value="Real Estate">Real Estate</MenuItem>
                                    <MenuItem value="Financial Services">
                                        Financial Services
                                    </MenuItem>
                                    <MenuItem value="Coaching">Coaching</MenuItem>
                                    <MenuItem value="Luxury Retail">Luxury Retail</MenuItem>
                                    <MenuItem value="B2B Services">B2B Services</MenuItem>
                                    <MenuItem value="Other">Other</MenuItem>
                                </Select>

                                <FormHelperText>
                                    {errors.industry?.message}
                                </FormHelperText>
                            </FormControl>
                        )}
                    />

                    {/* Average Deal Size Field */}
                    <Controller
                        name="dealSize"
                        control={control}
                        render={({ field }) => (
                            <FormControl
                                fullWidth
                                sx={{ mt: 2, ...luxuryField }}
                                error={!!errors.dealSize}
                            >
                                <Select
                                    {...field}
                                    displayEmpty
                                    sx={{
                                        color: field.value ? "#111" : "#9e9e9e",
                                        "& .MuiSelect-select": {
                                            padding: "16.5px 14px",
                                        },
                                    }}
                                    renderValue={(selected) => {
                                        if (!selected) {
                                            return (
                                                <span style={{ color: "#9e9e9e", fontSize: "14px" }}>
                                                    Average Deal Size Currently Closed
                                                </span>
                                            );
                                        }
                                        return selected;
                                    }}
                                >
                                    <MenuItem disabled value="">
                                        Average Deal Size Currently Closed
                                    </MenuItem>
                                    <MenuItem value="Under ₹10L">Under ₹3cr</MenuItem>
                                    <MenuItem value="₹10L–₹50L">₹3–₹5cr</MenuItem>
                                    <MenuItem value="₹50L+">₹5cr+</MenuItem>
                                </Select>

                                <FormHelperText>
                                    {errors.dealSize?.message}
                                </FormHelperText>
                            </FormControl>
                        )}
                    />
                </Grid>

                <Grid item xs={12} md={6}>
                    <Controller
                        name="challenge"
                        control={control}
                        render={({ field }) => (
                            <BoxInput
                            sx={{ mt: 1.8, ...luxuryField }}
                                placeholder="Biggest Closing Challenge"
                                multiline
                                rows={1.6}
                                fullWidth
                                {...field}
                                error={!!errors.challenge}
                                helperText={errors.challenge?.message}
                            />
                        )}
                    />

                    <Controller
                        name="goal"
                        control={control}
                        render={({ field }) => (
                            <BoxInput
                                placeholder="Improve in next 6 months?"
                                multiline
                                rows={1.6}
                                fullWidth
                                {...field}
                                error={!!errors.goal}
                                helperText={errors.goal?.message}
                                sx={{ mt: 2, ...luxuryField }}
                            />
                        )}
                    />

                    <Typography variant="h6" sx={{ mt: 3, fontSize: { xs: "14px", sm: "16px" } }}>
                        Ready to commit to weekly implementation?
                    </Typography>

                    <Controller
                        name="commitment"
                        control={control}
                        render={({ field }) => (
                            <RadioGroup row {...field}>
                                <FormControlLabel value="Yes" control={<Radio />} label="Yes" />
                                <FormControlLabel
                                    value="Need clarity"
                                    control={<Radio />}
                                    label="Need clarity"
                                />
                            </RadioGroup>
                        )}
                    />
                    {/* DatePicker Styled Like BoxInput */}
                    <Controller
                        name="startMonth"
                        control={control}
                        render={({ field }) => (
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <DatePicker
                                    value={field.value}
                                    onChange={(date) => field.onChange(date)}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            fullWidth
                                            error={!!errors.startMonth}
                                            helperText={errors.startMonth?.message}
                                        />
                                    )}
                                />
                            </LocalizationProvider>
                        )}
                    />
                </Grid>
            </Grid>

            <Divider sx={{ my: 5 }} />

            <Button
                fullWidth
                type="submit"
                variant="contained"
                // onClick={handleSubmit}
                disabled={mutation.isPending}
                sx={{
                    py: 2,
                    borderRadius: "16px",
                    background:
                        "linear-gradient(135deg, #0f63a5 0%, #1c88e5 100%)",
                }}
            >
                {mutation.isPending ? (
                    <CircularProgress size={24} />
                ) : (
                    "Submit Application"
                )}
            </Button>
        </Box>
    );
};

export default HighTicketForm;